import { useAppSelector } from '@/redux/hooks'
import classnames from 'classnames/bind'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { MouseEvent } from 'react'

// Libs
import { isLiveMallClub } from '@/constants/systemType'
import useBackRef from '@/hooks/useBackRef'
import useI18n from '@/hooks/useI18n'
import { handleGtmClickCustomService } from '@/methods/handleGtmEvent'
import { execute } from '@tutorabc/webview-jsbridge'

// Components
import Icon from '@/assets/icons'
import GoBackButton from '@/components/common/livemallclub/GoBackButton'
import MerchantInfo from '@/components/pages/Product/component/MerchantInfo'
import Empty from '@/components/shared/Empty'
import GoBackNavi from '@/components/shared/GoBackNavi'
import useMedia from '@/hooks/useMedia'
import MobileActionsFooter from '../MobileActionsFooter'
import RecommendedLives from './RecommendedLives'

// Constants
import { EXTERNAL_LINK } from '@/constants/externalLink'
import { GTM_LOCATION } from '@/constants/gtmEvent'
import { LIVE_MALL_CLUB_ROUTE_PATH, ROUTE_PATH } from '@/constants/routePath'
import { isClientSideShopApp } from '@/utils/navigator'
import { BRIDGE_HANDLER } from '@/constants/bridgeHandler'

// Styles
import styles from './style.module.scss'

// Mock Data
interface InvalidProductProps {
  merchantProductList?: any[]
  merchantInfo?: any
  liveData?: any
}

const cx = classnames.bind(styles)

const InvalidProduct = ({ merchantProductList, merchantInfo }: InvalidProductProps) => {
  const router = useRouter()
  const { t } = useTranslation()
  const { isOverseaUser } = useI18n()
  const { isDesktop } = useMedia()
  const previousPage = useAppSelector((state) => state.common.previousPage)
  // https://tutorabc-org.atlassian.net/browse/SDC-2109
  const { backRefPath, isBackRefResolved } = useBackRef()

  const handleContactUsClick = (e: MouseEvent<HTMLDivElement>) => {
    if (isLiveMallClub) return
    if (isClientSideShopApp) {
      e.preventDefault()
      execute({
        handlerName: BRIDGE_HANDLER.OPEN_LINE_OFFICIAL_ACCOUNT,
      })
    }
    handleGtmClickCustomService({ location: GTM_LOCATION.PRODUCT_DETAIL })
  }

  return (
    <>
      {isLiveMallClub && <GoBackButton onClick={() => router.back()} />}
      <div className={cx('product', { isLiveMallClub })}>
        <div className={cx('container')}>
          {!isLiveMallClub && (
            <GoBackNavi
              title={isBackRefResolved ? (backRefPath || previousPage ? t('Common_Back_Previous') : t('Common_Back_Mall')) : ''}
              className={cx('product-back')}
              staticLink={ROUTE_PATH.MALL}
              onClick={backRefPath ? () => router.push(backRefPath) : undefined}
            />
          )}
          <div className={cx('product-info-wrapper')}>
            <div className={cx('product-info')}>
              <Empty
                title={isLiveMallClub ? t('Product_NotExist_LiveMallClub') : t('Product_NotExist')}
                {...(isLiveMallClub && { icon: <Icon.ShopEmpty /> })}
                buttonUrl={isLiveMallClub ? LIVE_MALL_CLUB_ROUTE_PATH.EXPLORE : undefined}
              />
            </div>
          </div>

          {/* 商家資訊 */}
          {merchantProductList && merchantProductList.length > 0 && (
            <MerchantInfo
              isMerchantLive={false}
              memberInfoId={merchantInfo?.memberInfoId}
              avatar={merchantInfo?.headshotUrl}
              name={merchantInfo?.storeName}
              productTotalNumber={merchantInfo?.totalProduct}
              merchantProductList={merchantProductList}
            />
          )}
          {/* 商家資訊 */}
        </div>
        {!isDesktop && (
          <MobileActionsFooter
            storeUrl={`${ROUTE_PATH.USER}/${merchantInfo?.memberInfoId}`}
            contactUsUrl={isOverseaUser ? EXTERNAL_LINK.CUSTOMER_SERVICE_CENTER_HK : EXTERNAL_LINK.CUSTOMER_SERVICE_CENTER}
            onContactUsClick={handleContactUsClick}
            addToCartButtonProps={{
              disabled: true,
            }}
            buyNowButtonProps={{
              disabled: true,
            }}
          />
        )}

        {isLiveMallClub && <RecommendedLives />}
      </div>
    </>
  )
}
export default InvalidProduct
